import CameraStationPreview from "@/modules/communication/private/CameraStationPreview";
import { dataSyncEmitter } from "@/modules/events/emitter";
import Xmpp from "@/modules/Xmpp";
import EventEmitter from "@/lib/EventEmitter";
import { useStation } from "camera/store/station";
import { getIsPaired, getCanRecord } from "camera/store/station/selectors";
import EventManager from "camera/modules/events/EventManager";
import LocalEvents from "camera/modules/LocalEvents";
import BatteryUpdater from "./BatteryUpdater";
import PreviewUpdater from "./PreviewUpdater";
import AudioPlayer from "camera/lib/Media/AudioPlayer";

export default class StationManager {
  status: "idle" | "running" = "idle";
  private get canStart() {
    return getIsPaired(useStation.getState()) && getCanRecord(useStation.getState());
  }
  private xmpp = Xmpp.getInstance();
  private batteryUpdater: BatteryUpdater;
  private previewUpdater: PreviewUpdater;
  private emitter = new EventEmitter();
  on = this.emitter.on;
  off = this.emitter.off;

  constructor() {
    this.batteryUpdater = new BatteryUpdater(this.notifyViewers, this.emitter);
    this.previewUpdater = new PreviewUpdater(this.notifyViewers);
  }

  init = () => {
    useStation.subscribe(this.handleStoreUpdate);
    if (this.canStart) {
      log.media("camera paired, about to start camera updater");
      this.start();
    } else {
      log.media("camera not paired, not starting camera updater");
    }
  };

  private start = async () => {
    log.media("starting camera updater");
    this.status = "running";
    await this.previewUpdater.uploadVideoPreview();
    this.previewUpdater.startTakingVideoPreviews();
    this.batteryUpdater.startListeningForChanges();
    dataSyncEmitter.on("event-state-change", this.handleEvent);
  };

  private stop = () => {
    log.media("stopping camera updater");
    this.status = "idle";
    this.previewUpdater.stopTakingVideoPreviews();
    this.batteryUpdater.stopListeningForChanges();
    dataSyncEmitter.off("event-state-change", this.handleEvent);
  };

  private handleEvent = async (e: CameraEvent) => {
    if (!e.finished) {
      await this.previewUpdater.uploadVideoPreview({ notify: false });
      this.previewUpdater.restartInterval();
    }

    this.notifyViewers();
  };

  private handleStoreUpdate = (store: StationStore) => {
    const canProcess = getIsPaired(store) && getCanRecord(store);

    if (canProcess && this.status === "idle") this.start();
    if (!canProcess && this.status === "running") this.stop();
  };

  private notifyViewers = async () => {
    const payload = await this.getCurrentStatePayload();
    log.xmpp("Sending presence payload update", payload);
    this.xmpp.sendPresence(undefined, payload);
  };

  getCurrentStatePayload = async () => {
    const message = CameraStationPreview.encode(await this.getCurrentState());
    const payload: CameraCurrentStatePayload = { currentState: message };
    return payload;
  };

  getBatteryStatus = () => this.batteryUpdater.getBatteryStatePayload();

  getAlarmStatus = (): CameraAlarmStatus => {
    return { state: AudioPlayer.isPlaying ? "ALARM_ON" : "ALARM_OFF" };
  };

  getCurrentState = async (): Promise<CameraStationPreview> => ({
    status: {
      lastEvent: EventManager.getLastActiveEvent() || (await LocalEvents.getFirstFound()),
      batteryStatus: this.getBatteryStatus(),
      currentEvents: EventManager.getCurrentEvents() || []
    },
    image: this.previewUpdater.getPreviewImagePayload()
  });
  getDetectionZonesPreview = () => this.previewUpdater.getDetectionZonesPreview();
}
